/**
 * Generate default config for platform
 */
import type { Maybe, Menu, ModalService, ThemeService } from '@evc/web-components';
import { BtnActionEnum } from '@evc/web-components';

import { AboutModalComponent } from '../../components/about-modal/about-modal.component';
import type { I18nPlatformService } from '../i18n/i18n.service';
import type { ApiConfig, ApplicationURIs, I18nConfig, PlatformConfig } from './config.type';
import { CORE_API_ENDPOINTS, DEVICE_API_ENDPOINTS, STORAGE_API_ENDPOINTS } from './config.type';
import { type AvailableLanguages, ENVIRONMENT, type PlatformEnv } from './env.type';

export function defaultPlatformConfigFactory(
  environment: PlatformEnv,
  themeService: ThemeService,
  modalService: ModalService,
  i18nPlatformService: I18nPlatformService,
): Partial<PlatformConfig> {
  const helpMenu: Menu = {
    title: i18nPlatformService.t$('menus.help.title'),
    items: [
      {
        // ? idea : add optional key `enabled` - if defined as false will skip this item
        text: i18nPlatformService.t$('menus.help.about'),
        key: 'about',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: (): void => {
            modalService.open$(AboutModalComponent).subscribe();
          },
        },
      },
    ],
  };

  /** default setings will define theme management
   * - may be merge with extra menuItems (rendered before themes)
   * - handle item with key `advanced-settings` to be forced to be last (after themes)
   */
  const themes = ['light', 'dark', 'auto'] as const;
  const settingsMenu: Menu = {
    title: i18nPlatformService.t$('menus.settings.title'),
    items: [
      {
        text: i18nPlatformService.t$('menus.settings.themes.title'),
        key: 'theme',
        collapsible: themes.map((theme) => ({
          text: i18nPlatformService.t$(`menus.settings.themes.${theme}`),
          key: theme,
          img: `/assets/theme/${theme}.webp`,
          selected: theme === 'auto',
          action: {
            type: BtnActionEnum.CALLBACK,
            callbackOrUrl: (): void => {
              themeService.setTheme(theme);
            },
          },
        })),
      },
    ],
  };

  const leftbarMenus: Menu[] = [];

  const env = environment.ENV;

  return {
    env,
    /** application basic infos
     * used in metas, logo in navbar etc
     *
     * + IDEAS : favicon, keywords, description...
     */
    app: {
      name: 'evident-connect',
      title: '', // Using imagotype (image + text) for logo
      logo: '/assets/logos/evident-connect-imagotype.svg#evident-connect-imagotypeconnect-imagotype',
    },
    menus: {
      settings: settingsMenu,
      help: helpMenu,
      leftbar: leftbarMenus,
    },
    applications: [],
    uris: getUrisConfig(environment),
    theme: 'auto',
    monitoring: undefined,
    fallbackApplicationIcon: '/assets/applications/generic-app.svg',
  };
}

/** specific links for some specific buttons in topbar
 * - if not defined - will compute those per env (see config.service)
 * - if defined (input) will be overrided
 */
export function getUrisConfig(environment: PlatformEnv): ApplicationURIs {
  const { hostname } = window.location;
  const { ENV, AUTH_USE_LEGACY_DOMAIN } = environment;
  const forceLegacy = hostname === 'localhost' && AUTH_USE_LEGACY_DOMAIN;

  const domain = (() => {
    switch (ENV) {
      case ENVIRONMENT.connect_local:
      case ENVIRONMENT.connect_testing:
        return 'localhost';
      case ENVIRONMENT.local:
      case ENVIRONMENT.testing:
      case ENVIRONMENT.development:
        return forceLegacy || /^(?!connect\.)([\w-]+\.)?dev-evcnt\.com$/.test(hostname)
          ? 'dev-evcnt.com'
          : 'connect.dev-evcnt.com';
      case ENVIRONMENT.staging:
        return forceLegacy || /^(?!connect\.)([\w-]+\.)?stg-evcnt\.com$/.test(hostname)
          ? 'stg-evcnt.com'
          : 'connect.stg-evcnt.com';
      default:
        return forceLegacy || /tnmconnect\.com$/.test(hostname)
          ? 'tnmconnect.com'
          : 'connect.evidentscientific.com';
    }
  })();

  const isLegacyDomain = !domain.startsWith('connect.');

  const perEnvUris = (() => {
    switch (ENV) {
      case ENVIRONMENT.connect_local:
      case ENVIRONMENT.connect_testing:
        return {
          homepage: `http://${domain}:8080`,
          marketplace: `http://${domain}:8080/marketplace`,
          organization: `http://${domain}:8081`,
          user: `http://${domain}:8082`,
          admin: `http://${domain}:8083`,
          drive: `http://${domain}:8084`,
          equipment: `http://${domain}:8085`,
          licenses: `http://${domain}:8087`,
        };
      default:
        return {
          homepage: isLegacyDomain ? `https://home.${domain}` : `https://${domain}`,
          marketplace: isLegacyDomain
            ? `https://home.${domain}/marketplace`
            : `https://${domain}/marketplace`,
          organization: `https://organization.${domain}`,
          user: `https://user.${domain}`,
          admin: `https://admin.${domain}`,
          equipment: `https://equipment.${domain}`,
          drive: `https://drive.${domain}`,
          licenses: `https://licenses.${domain}`,
        };
    }
  })();

  return {
    root: window?.location.origin ?? '/',
    public: perEnvUris.marketplace,
    logout: perEnvUris.homepage,
    ...perEnvUris,
  };
}

/** compute clean config for api based on some .env config */
export function getApiConfig(environment: PlatformEnv): Maybe<ApiConfig> {
  if (!environment.USE_CONNECT_PLATFORM) return undefined;

  return {
    uris: environment.PROTECTED_URIS!,
    endpoints: {
      ...CORE_API_ENDPOINTS,
      ...STORAGE_API_ENDPOINTS,
      ...DEVICE_API_ENDPOINTS,
    },
  };
}

export function getI18nConfig(environment: PlatformEnv): I18nConfig {
  const PLATFORM_LANGUAGES = ['en', 'fr'] as AvailableLanguages[];
  const { LANG, LOCALES, LOCALES_STORAGE_KEY } = environment;
  const locales = LOCALES ?? PLATFORM_LANGUAGES;
  const storageKey = LOCALES_STORAGE_KEY!;
  const languages = Array.from(new Set(locales.map((locale: string) => locale.split('-')[0])));

  return {
    locales,
    storageKey,
    languages,
    lang: LANG,
    defaultLang: LANG ?? languages[0],
    platformLanguages: PLATFORM_LANGUAGES,
  };
}
